import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Rocket } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@reflex/ui/components/ui/command';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { Label } from '@reflex/ui/components/ui/label';
import { cn } from '@reflex/ui/lib/utils';
import { useSetLaunchDefaultAgent, useSetAgentDefault } from '@/hooks/config/useAgentModelPolicy';
import type { AdminAgentModelsResponse, DiscoveredModel } from '@reflex/shared';

const SYSTEM_DEFAULT_VALUE = '__system_default__';
const MODEL_NONE_SENTINEL = '__none__';
const LAUNCH_DEFAULTS_DESCRIPTION =
  'Choose which agent type and model are preselected when users open the Launch dialog. Programmatic launches must still specify an agent type explicitly.';

interface LaunchDefaultsPanelProps {
  scope: 'org';
  adminData: AdminAgentModelsResponse;
  readOnly?: boolean;
}

function isPolicyEnabled(record: Record<string, boolean>, id: string): boolean {
  return record[id] !== false;
}

export function LaunchDefaultsPanel({
  scope,
  adminData,
  readOnly = false,
}: LaunchDefaultsPanelProps) {
  const setLaunchDefault = useSetLaunchDefaultAgent();
  const setAgentDefault = useSetAgentDefault();
  const [modelPopoverOpen, setModelPopoverOpen] = useState(false);

  const configuredValue = adminData.orgLaunchDefaultAgentType;

  // Only show source badge when there's an effective agent type (not when all agents are disabled)
  const hasEffectiveAgentType = adminData.effectiveLaunchDefaultAgentType != null;

  // Source every registered agent type from `launchableAgents` (PRD-24) so
  // single-model agents can be picked as the launch default too. Fall back to
  // the multi-model `agents` list for older servers that haven't populated
  // launchableAgents yet.
  const agentTypes = (adminData.launchableAgents ?? []).length
    ? adminData.launchableAgents!.map((a) => ({
        id: a.agentType,
        displayName: a.displayName,
        enabled: a.effectiveEnabled,
        multiModel: a.multiModel,
      }))
    : adminData.agents.map((a) => ({
        id: a.agentType,
        displayName: a.displayName,
        enabled: true,
        multiModel: true,
      }));

  const handleAgentChange = (value: string) => {
    if (readOnly) return;
    if (value === SYSTEM_DEFAULT_VALUE) {
      setLaunchDefault.mutate({ scope, agentType: undefined });
    } else {
      setLaunchDefault.mutate({ scope, agentType: value });
    }
  };

  // Coerce a stale configured value (referring to a type no longer in the
  // registry) so the Select doesn't display an orphaned `value` that doesn't
  // match any item.
  const hasConfiguredOption =
    configuredValue != null && agentTypes.some((a) => a.id === configuredValue);
  const selectValue = hasConfiguredOption ? configuredValue! : SYSTEM_DEFAULT_VALUE;

  // Determine the agent type used for model selection (the configured value if present)
  const selectedAgentType = selectValue !== SYSTEM_DEFAULT_VALUE ? selectValue : null;
  const selectedAgentInfo = selectedAgentType
    ? agentTypes.find((a) => a.id === selectedAgentType)
    : null;
  const isMultiModelAgent = selectedAgentInfo?.multiModel ?? false;

  // Find the AdminAgentModelEntry for the selected agent (only exists for multi-model agents)
  const agentEntry = selectedAgentType
    ? adminData.agents.find((a) => a.agentType === selectedAgentType)
    : null;

  // Get policy-filtered models for the selected agent
  const filteredModels: DiscoveredModel[] = useMemo(() => {
    if (!agentEntry) return [];
    const policy = agentEntry.effectivePolicy;
    const enabledProviderIds = new Set(
      agentEntry.providers.filter((p) => isPolicyEnabled(policy.providers, p.id)).map((p) => p.id),
    );
    return (agentEntry.discoveredModels ?? []).filter(
      (m) => enabledProviderIds.has(m.providerId) && isPolicyEnabled(policy.models, m.id),
    );
  }, [agentEntry]);

  // Get current default model for the selected agent
  const currentDefaultModelId =
    agentEntry?.effectiveDefault?.origin !== 'unset'
      ? agentEntry?.effectiveDefault?.modelId
      : undefined;
  const currentDefaultModel = currentDefaultModelId
    ? filteredModels.find((m) => m.id === currentDefaultModelId)
    : undefined;

  // Determine model picker state
  const isSystemDefault = selectValue === SYSTEM_DEFAULT_VALUE;
  const isSingleModel = selectedAgentType && !isMultiModelAgent;
  const hasNoModels = isMultiModelAgent && filteredModels.length === 0;
  const isModelPickerDisabled =
    readOnly || isSystemDefault || isSingleModel || hasNoModels || setAgentDefault.isPending;

  // Get hint message for disabled state
  const getDisabledHint = (): string | null => {
    if (isSystemDefault) return 'Select a default agent to choose its default model.';
    if (isSingleModel) return 'Single-model agent: no model selection.';
    if (hasNoModels) return 'No models available after policy filtering.';
    return null;
  };
  const disabledHint = getDisabledHint();

  const handleModelChange = (modelId: string) => {
    if (readOnly || !selectedAgentType) return;

    if (modelId === MODEL_NONE_SENTINEL) {
      setAgentDefault.mutate({
        agentType: selectedAgentType,
        defaults: { modelId: undefined, providerId: undefined },
        scope,
      });
    } else {
      const model = filteredModels.find((m) => m.id === modelId);
      if (model) {
        setAgentDefault.mutate({
          agentType: selectedAgentType,
          defaults: { modelId: model.id, providerId: model.providerId },
          scope,
        });
      }
    }
    setModelPopoverOpen(false);
  };

  const isPending = setLaunchDefault.isPending || setAgentDefault.isPending;

  return (
    <SectionCard
      title="Launch defaults"
      description={LAUNCH_DEFAULTS_DESCRIPTION}
      icon={<Rocket />}
      action={
        !hasEffectiveAgentType ? (
          <span className="text-2xs text-muted-foreground">(all agents disabled)</span>
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <Label className="text-xs text-muted-foreground mb-1.5 block">Default Agent</Label>
          <Select
            value={selectValue}
            onValueChange={handleAgentChange}
            disabled={readOnly || isPending}
          >
            <SelectTrigger
              className="text-xs h-8 w-full"
              aria-label="Select default agent"
              data-testid="launch-defaults-agent"
            >
              <SelectValue placeholder="System default" />
            </SelectTrigger>
            <SelectContent className="**:data-[slot=select-item]:text-xs">
              <SelectItem value={SYSTEM_DEFAULT_VALUE}>System default</SelectItem>
              {agentTypes.map((agent) => (
                <SelectItem key={agent.id} value={agent.id} disabled={!agent.enabled}>
                  {agent.displayName}
                  {!agent.enabled && <span className="ml-2 text-muted-foreground">(disabled)</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-0">
          <Label className="text-xs text-muted-foreground mb-1.5 block">Default Model</Label>
          {disabledHint ? (
            <p className="text-2xs text-muted-foreground py-2">{disabledHint}</p>
          ) : (
            <Popover open={modelPopoverOpen} onOpenChange={setModelPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={modelPopoverOpen}
                  aria-label="Select default model"
                  disabled={isModelPickerDisabled}
                  data-testid="launch-defaults-model"
                  className={cn(
                    'flex h-8 w-full items-center justify-between gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs shadow-xs transition-[color,box-shadow] outline-none',
                    'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
                    'dark:bg-input/30 dark:hover:bg-input/50',
                    isModelPickerDisabled && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <span className="truncate">
                    {currentDefaultModel?.displayName ?? 'None (system decides)'}
                  </span>
                  <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search models…" className="h-8 text-xs" />
                  <CommandList>
                    <CommandEmpty className="py-3 text-xs">No models found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__none_system_decides__"
                        onSelect={() => handleModelChange(MODEL_NONE_SENTINEL)}
                        className="text-xs"
                      >
                        <Check
                          className={cn(
                            'mr-1.5 h-3 w-3 shrink-0',
                            !currentDefaultModelId ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        None (system decides)
                      </CommandItem>
                      {filteredModels.map((model) => (
                        <CommandItem
                          key={model.id}
                          value={model.displayName}
                          onSelect={() => handleModelChange(model.id)}
                          className="text-xs"
                        >
                          <Check
                            className={cn(
                              'mr-1.5 h-3 w-3 shrink-0',
                              currentDefaultModelId === model.id ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {model.displayName}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
